import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTrack, useVariationDetail } from '@hackler/react-sdk';

import { AppContext } from '../../App';
import { appRoutes } from '../../AppRoutes';
import { getUser, postAcceptInvite, postSignIn, postSignUpBySocial } from '../api/apiClient';
import { getSavedAuth, setLastLoginType, setSavedAuth } from './storage';
import { doRedirect, getFallbackRedirect, isDevEnv } from './utils';
import type { AuthResponse } from '../../shared/types/responseTypes';
import { LoginController, Social } from '../providers_services/LoginProvider';
import { AgreementsCheck } from '../../pages/login/components/SignUpAgreements';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isValidId = (input: string) => {
  const v = input.trim();
  if (!v) return false;
  return EMAIL_RE.test(v);
};

export const useLoginController = (opts: { formRef: React.RefObject<HTMLFormElement> }): LoginController => {
  const { formRef } = opts;

  const navigate = useNavigate();
  const { redirect, invite } = useContext(AppContext);

  const track = useTrack();
  const leadText = useVariationDetail(8).get('auth-readtext', '지원자에게 더 부드럽고 편안하게 연락하세요.');

  const [userId, _setUserId] = useState(''); // 내부 setter 숨기고 래핑
  const [password, setPassword] = useState('');
  const [userIdError, setUserIdError] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  const [social, setSocial] = useState<Social>();
  const [name, setName] = useState('');
  const [agreements, setAgreements] = useState<AgreementsCheck>();

  useEffect(() => {
    track({ key: 'view_login' });
  }, []);

  useEffect(() => {
    const auth = getSavedAuth();
    if (!auth) return;
    if (tokenExpired(auth) || !auth.access_token) {
      setSavedAuth(null);
      return;
    }
    handleLoginSuccess(auth);
  }, []);

  useEffect(() => {
    if (social?.email) _setUserId(social.email);
    if (social?.name) setName(social.name);
  }, [social]);

  const setUserId = (v: string) => {
    _setUserId(v);
    const trimmed = v.trim();
    if (!trimmed) {
      setUserIdError(undefined);
      return;
    }
    if (!isValidId(trimmed)) {
      setUserIdError('올바른 아이디(이메일)을 입력해주세요');
    } else {
      if (userIdError) setUserIdError(undefined);
    }
  };

  const handleInviteAccept = async () => {
    if (!invite) return;
    const auth = getSavedAuth();
    if (!auth) throw new Error('실행 권한이 없습니다.\n확인 후 다시 시도해주세요.');
    await postAcceptInvite(invite.token);
    doRedirect(redirect, auth);
  };

  const handleLoginSuccess = (auth: AuthResponse) => {
    setSavedAuth(auth);
    getUser(auth.access_token)
      .then(user => {
        if (user.companies.length) {
          setSavedAuth(auth);
          doRedirect(redirect ?? getFallbackRedirect(), auth);
        } else if (invite) {
          void handleInviteAccept();
        } else {
          navigate(appRoutes.welcome.path());
        }
      })
      .catch(() => {
        console.error('AUTH ERROR');
        setSavedAuth(null);
      });
  };

  const submitEmailPassword = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidId(userId)) {
      setUserIdError('올바른 아이디(이메일)을 입력해주세요');
      return;
    }

    setLastLoginType('email');
    postSignIn(userId, password)
      .then(handleLoginSuccess)
      .catch((code?: number) => {
        if (code && code === 404) {
          setUserIdError('가입되지 않은 이메일이에요');
          setPasswordError(undefined);
        } else {
          setPasswordError('아이디와 비밀번호가 일치하지 않습니다');
          setUserIdError(undefined);
        }
      });
  };

  const submitSocialSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!social || !agreements) return;

    if (!isValidId(userId)) {
      setUserIdError('올바른 아이디(이메일)을 입력해주세요');
      return;
    }

    const auth = await postSignUpBySocial(
      redirect ?? getFallbackRedirect(),
      social.provider,
      social.access_token,
      name || social.name || '',
      agreements.terms,
      agreements.privacy,
      !!agreements.service,
    );
    handleLoginSuccess(auth);
  };

  const onToken = () => {
    const auth = getSavedAuth();
    if (auth) handleLoginSuccess(auth);
  };

  const goSignUp = () => navigate(appRoutes.signUp.path());

  return {
    leadText,
    formRef,

    social,
    setSocial,
    showApple: isDevEnv(),

    userId,
    setUserId,
    password,
    setPassword,
    name,
    setName,
    agreements,
    setAgreements,

    userIdError,
    passwordError,

    submitEmailPassword,
    submitSocialSignUp,
    goSignUp,
    onToken,
  };
};

export const tokenExpired = (auth: AuthResponse): boolean => {
  if (!auth?.expired_at) return true;
  const timeout = dayjs(auth.expired_at).diff(dayjs(), 'milliseconds');
  return isNaN(timeout) ? true : timeout <= 60000;
};
